import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Checkbox, Empty, Input, Popover, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  ExportOutlined,
  MergeCellsOutlined,
  SearchOutlined, StarOutlined,
} from '@ant-design/icons';
import { usePermissions } from '../contexts/PermissionContext';
import { useNavigate } from 'react-router-dom';
import GcPagina from '../components/Layout/GcPagina';
import { FctIcono, FctTarjeta, fmtFecha, fmtNumero } from '../fct/react';
import { fct, type FctFamiliaEstado } from '../fct/tokens/tokens';
import type { CatalogosAltas } from '../api/altasCasosApi';
import { interpretarError } from '../api/altasCasosApi';
import {
  CLASIFICACION_LABELS,
  ClasificacionCliente,
  ClienteGridFila,
  TipoRegistroGrid,
  consultarClientes,
  leerCatalogosGrid,
} from '../api/clientesGridApi';
import { CrearClienteDrawer } from '../components/Clientes/CrearClienteDrawer';

const { Text } = Typography;

/** Centinela de los selects de filtro: -1 = "todos" (AntD no distingue undefined de "sin valor"). */
const TODOS = -1;

/** Lo que el usuario tiene puesto en los controles, aplicado o no. */
interface Criterios {
  busqueda: string;
  idCodigo: string;
  idProvincia: number;
  inversor: number;
  /** -1 todos · 1 titular vigente de farmacia · 0 no titular. */
  farmacia: number;
  tipoRegistro: TipoRegistroGrid;
  // --- Filtros de "segundo nivel" (popover "...", ajuste del Dev 2026-09-18): menos frecuentes
  // que los principales, no ocupan banda propia. Forman parte del mismo pendientes/aplicados,
  // así que se aplican igual (lupa/Enter dentro del popover, o el botón Buscar general).
  /** -1 todas · 0-4 = GC_C_TIPO_CLIENTE. Filtro que existía antes de D-GC-64 y volvió aquí. */
  clasificacion: number;
  /** Contiene, entre TODOS los teléfonos del cliente (no solo el principal de la columna). */
  telefono: string;
  /** Prefijo (los códigos postales son jerárquicos: los primeros dígitos son la provincia). */
  codigoPostal: string;
  /** Contiene. */
  poblacion: string;
  /** Desmarcado por defecto (D-GC-73): sin él, los clientes de baja no se listan. */
  incluirBajas: boolean;
}

const SIN_CRITERIOS: Criterios = {
  busqueda: '',
  idCodigo: '',
  idProvincia: TODOS,
  inversor: TODOS,
  farmacia: TODOS,
  tipoRegistro: TipoRegistroGrid.Todos,
  clasificacion: TODOS,
  telefono: '',
  codigoPostal: '',
  poblacion: '',
  incluirBajas: false,
};

/**
 * Grid de clientes con alta directa (F1.2, spec §3.2; GESCL-T04 tramo 5). Equivalente Gescom del
 * grid de Auraquantic: la herramienta de Marketing y Sistemas para casos especiales e incidencias
 * sin recurrir a Management Studio.
 *
 * Todo el filtrado y la paginación son de SERVIDOR: la base son ~90.000 clientes, así que aquí
 * nunca se carga más de una página (el backend acota el tamaño además de esta pantalla).
 *
 * **Los filtros NO se aplican al tocarlos** (ajuste del Dev, 2026-09-18). La consulta sobre esta
 * base tarda entre 40 ms y 2 s según el criterio; si cada clic lanzara una búsqueda, acotar
 * obligaría a esperar a cada paso. Se aplican: con la lupa o Enter en "Nombre/apellidos" y en
 * "ID o código" (donde el gesto natural es teclear y buscar) y con el botón **Buscar** para todo
 * lo demás. Mientras haya cambios sin aplicar, el botón lo indica con un punto.
 *
 * Además se muestra **qué filtros están aplicados**: los filtros se combinan con Y, así que un
 * filtro olvidado puede hacer que una búsqueda correcta no devuelva nada. Verlos evita el susto.
 *
 * v1 = consultar + crear (§3.2 "Límites del grid v1"). Editar y eliminar se moverán a la ficha
 * del cliente, fuera de esta tarea (D-GC-63); "Exportar a Excel" se deja **visible y deshabilitado
 * con el motivo en el tooltip** (D28, fuera de v1): que el usuario vea por qué no está, en vez de
 * no encontrarlo.
 */
/**
 * Marca discreta de clasificación (D-GC-57 en pantalla, ajuste del Dev 2026-09-18): un punto de
 * color junto al nombre para Atención preferente / Controversia / Competencia. Normal y "sin
 * determinar" (el 96 % de la base) NO llevan marca — es una señal de excepción, no una columna
 * más; por eso no se reintroduce como columna ni como filtro (eso sigue siendo D-GC-64, Farmacia).
 */
/**
 * Clasificaciones especiales que llevan marca junto al nombre. Son una categoría, pero el responsable
 * pidió conservar la señal visual (aceptación GCVSC-I001, 2026-10-04): cada una toma la familia del
 * semáforo (ajuste de Javier, 2026-10-04: Atención preferente → aviso con estrella; Controversia → proceso
 * y Competencia → riesgo, ambas con icono de alarma) y el tooltip dice cuál es.
 */
const FAMILIA_CLASIFICACION: Partial<Record<number, FctFamiliaEstado>> = {
  [ClasificacionCliente.AtencionPreferente]: 'aviso',
  [ClasificacionCliente.Controversia]: 'proceso',
  [ClasificacionCliente.Competencia]: 'riesgo',
};

const ClientesGridPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // `pendientes` = lo que hay en los controles · `aplicados` = lo que se ha consultado.
  const [pendientes, setPendientes] = useState<Criterios>(SIN_CRITERIOS);
  const [aplicados, setAplicados] = useState<Criterios>(SIN_CRITERIOS);

  const [pagina, setPagina] = useState(1);
  const [tamanio, setTamanio] = useState(50);
  const [cargando, setCargando] = useState(false);
  const [clientes, setClientes] = useState<ClienteGridFila[]>([]);
  const [totalBase, setTotalBase] = useState(0);
  const [totalFiltrado, setTotalFiltrado] = useState(0);
  const [catalogos, setCatalogos] = useState<CatalogosAltas | null>(null);
  const [recienCreado, setRecienCreado] = useState<number | null>(null);
  // Sin carga automática al entrar (ajuste del Dev, 2026-09-18): el uso habitual es una
  // búsqueda concreta, no hojear la base entera — cargar de entrada solo sería una espera para
  // nada. Se carga la primera vez que el usuario aplica un criterio (lupa/Enter/Buscar) o crea
  // un cliente directo; a partir de ahí se comporta como antes (recarga en cada cambio aplicado
  // y en cada página).
  const [haBuscado, setHaBuscado] = useState(false);
  const [popoverAbierto, setPopoverAbierto] = useState(false);

  const patch = (p: Partial<Criterios>) => setPendientes((prev) => ({ ...prev, ...p }));

  const hayCambiosSinAplicar = useMemo(
    () => (Object.keys(SIN_CRITERIOS) as Array<keyof Criterios>)
      .some((k) => pendientes[k] !== aplicados[k]),
    [pendientes, aplicados],
  );

  /** Marca visual del botón "...": hay un filtro de segundo nivel ACTIVO ahora mismo, se esté
      editando o no (distinto del punto de "Buscar", que solo avisa de cambios sin aplicar). */
  const haySegundoNivelAplicado =
    aplicados.clasificacion !== TODOS
    || aplicados.telefono !== ''
    || aplicados.codigoPostal !== ''
    || aplicados.poblacion !== ''
    || aplicados.incluirBajas;

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const res = await consultarClientes({
        busqueda: aplicados.busqueda || undefined,
        idCodigo: aplicados.idCodigo || undefined,
        idProvincia: aplicados.idProvincia === TODOS ? undefined : aplicados.idProvincia,
        inversor: aplicados.inversor === TODOS ? undefined : aplicados.inversor === 1,
        titularFarmacia: aplicados.farmacia === TODOS ? undefined : aplicados.farmacia === 1,
        idTipoCliente: aplicados.clasificacion === TODOS ? undefined : aplicados.clasificacion,
        telefono: aplicados.telefono || undefined,
        codigoPostal: aplicados.codigoPostal || undefined,
        poblacion: aplicados.poblacion || undefined,
        incluirBajas: aplicados.incluirBajas,
        tipoRegistro: aplicados.tipoRegistro,
        pagina,
        tamanio,
      });
      setClientes(res.clientes);
      setTotalBase(res.totalBase);
      setTotalFiltrado(res.totalFiltrado);
    } catch (error) {
      message.error(interpretarError(error).mensaje);
    } finally {
      setCargando(false);
    }
  }, [aplicados, pagina, tamanio]);

  useEffect(() => {
    if (haBuscado) cargar();
  }, [cargar, haBuscado]);

  useEffect(() => {
    // Catálogos estables: una vez por sesión de pantalla. Si fallan, los filtros de provincia y
    // el alta quedan sin opciones, pero el grid sigue siendo utilizable.
    leerCatalogosGrid()
      .then(setCatalogos)
      .catch(() => message.warning('No se han podido cargar los catálogos (provincias, tramos, plazos).'));
  }, []);

  /** Aplica TODO lo que hay en los controles (lupa, Enter o botón Buscar). */
  const aplicar = (extra?: Partial<Criterios>) => {
    const nuevos = { ...pendientes, ...extra };
    setPendientes(nuevos);
    setAplicados(nuevos);
    setPagina(1);
    setHaBuscado(true);
  };

  /**
   * `Input.Search.onSearch` dispara también al pulsar la "X" de `allowClear` (con valor vacío),
   * no solo con Enter/lupa (ajuste del Dev, 2026-09-18: se veía como una recarga sorpresa al
   * borrar un filtro de 2º nivel para escribir otro). Se ignora esa fuente: borrar solo debe
   * dejar el campo vacío en `pendientes`, ya lo hace `onChange` — no aplicar hasta que el
   * usuario pulse Enter/lupa/Buscar de verdad.
   */
  const buscarSiNoEsLimpiar = (
    aplicarConValor: (valor: string) => void,
  ) => (valor: string, _event: unknown, info?: { source?: string }) => {
    if (info?.source === 'clear') return;
    aplicarConValor(valor);
  };

  /**
   * Solo vacía los CONTROLES (`pendientes`), no relanza la consulta (ajuste del Dev, 2026-09-18,
   * mismo espíritu que D-GC-71): el listado sigue mostrando lo último aplicado hasta que el
   * usuario fije los criterios nuevos y pulse Buscar/Enter/lupa — no tiene sentido esperar a una
   * recarga de toda la base justo para, acto seguido, esperar otra con el filtro de verdad.
   * `aplicar()` ya pone la página a 1 cuando llegue esa búsqueda; no hace falta tocarla aquí.
   */
  const limpiar = () => {
    setPendientes(SIN_CRITERIOS);
  };

  const provinciaAplicada = useMemo(
    () => catalogos?.provincias.find((p) => p.id === aplicados.idProvincia)?.descripcion,
    [catalogos, aplicados.idProvincia],
  );

  /** Filtros aplicados en texto, para que ninguno quede actuando sin que se vea. */
  const filtrosAplicados = useMemo(() => {
    const activos: string[] = [];
    if (aplicados.busqueda) activos.push(`texto "${aplicados.busqueda}"`);
    if (aplicados.idCodigo) activos.push(`ID/código "${aplicados.idCodigo}"`);
    if (aplicados.idProvincia !== TODOS) activos.push(provinciaAplicada ?? `provincia ${aplicados.idProvincia}`);
    if (aplicados.inversor !== TODOS) activos.push(aplicados.inversor === 1 ? 'inversor' : 'no inversor');
    if (aplicados.farmacia !== TODOS) activos.push(aplicados.farmacia === 1 ? 'titular de farmacia' : 'no titular de farmacia');
    if (aplicados.clasificacion !== TODOS) activos.push(CLASIFICACION_LABELS[aplicados.clasificacion]);
    if (aplicados.telefono) activos.push(`teléfono "${aplicados.telefono}"`);
    if (aplicados.codigoPostal) activos.push(`CP "${aplicados.codigoPostal}"`);
    if (aplicados.poblacion) activos.push(`población "${aplicados.poblacion}"`);
    if (aplicados.incluirBajas) activos.push('incluye bajas');
    if (aplicados.tipoRegistro === TipoRegistroGrid.Definitivos) activos.push('solo definitivos');
    if (aplicados.tipoRegistro === TipoRegistroGrid.Provisionales) activos.push('solo provisionales');
    if (aplicados.tipoRegistro === TipoRegistroGrid.DeBaja) activos.push('solo de baja');
    return activos;
  }, [aplicados, provinciaAplicada]);

  const opcionesProvincia = useMemo(
    () => [
      { label: 'Provincia: todas', value: TODOS },
      ...(catalogos?.provincias ?? []).map((p) => ({ label: p.descripcion, value: p.id })),
    ],
    [catalogos],
  );

  const columns: ColumnsType<ClienteGridFila> = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 130,
      render: (codigo: string | null, c) => (
        <span>
          <span className="fct-mono">{codigo ?? c.idCliente}</span>
          {c.provisional && (
            <Tooltip title="Alta web pendiente de validar: se trata en el listado de altas">
              <Tag style={{ marginLeft: 6 }}>Provisional</Tag>
            </Tooltip>
          )}
          {c.idCliente === recienCreado && (
            <Tag style={{ marginLeft: 6 }}>Nuevo</Tag>
          )}
        </span>
      ),
    },
    {
      title: 'ID',
      dataIndex: 'idCliente',
      key: 'idCliente',
      width: 90,
      className: 'fct-mono',
      // Se muestra porque en los migrados el código NO se corresponde con el ID (CL21345 es el
      // cliente 72962): sin la columna, buscar por ID es adivinar.
      render: (v: number) => <Text type="secondary">{v}</Text>,
    },
    {
      title: 'Nombre / Apellidos',
      key: 'nombre',
      render: (_, c) => {
        const nombreCompleto = `${c.nombre ?? ''} ${c.apellido1 ?? ''} ${c.apellido2 ?? ''}`.trim();
        const familiaClasif = c.idTipoCliente != null ? FAMILIA_CLASIFICACION[c.idTipoCliente] : undefined;
        const nombre = nombreCompleto || <Text type="secondary">—</Text>;
        return (
          <span>
            {familiaClasif && (
              <Tooltip title={c.tipoCliente ?? 'Clasificación especial'}>
                {c.idTipoCliente === ClasificacionCliente.AtencionPreferente
                  // Atención preferente con estrella (ajuste de Javier, 2026-10-04): mismo ámbar de la familia aviso.
                  ? <StarOutlined aria-label={c.tipoCliente ?? 'Atención preferente'} style={{ color: fct.estado[familiaClasif].linea, marginRight: 6, fontSize: 13 }} />
                  : <span aria-label={c.tipoCliente ?? 'Clasificación especial'} role="img" style={{ color: fct.estado[familiaClasif].linea, marginRight: 6, display: 'inline-flex', verticalAlign: 'middle' }}><FctIcono nombre="alerta" style={{ width: 14, height: 14 }} /></span>}
              </Tooltip>
            )}
            {/* De baja (D-GC-73): tooltip con la fecha sobre el propio nombre — la fila entera se
                resalta (onRow más abajo), así el nombre es donde el usuario mira primero. */}
            {c.fechaBaja ? (
              <Tooltip title={`De baja el ${fmtFecha(c.fechaBaja)}`}>
                <Text strong style={{ color: fct.estado.riesgo.texto }}>{nombre}</Text>
              </Tooltip>
            ) : nombre}
          </span>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Teléfono',
      dataIndex: 'telefono',
      key: 'telefono',
      width: 130,
      className: 'fct-mono',
      render: (v: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Provincia',
      dataIndex: 'provincia',
      key: 'provincia',
      width: 130,
      render: (v: string | null) => v || <Text type="secondary">—</Text>,
    },
    {
      title: 'Inversor',
      dataIndex: 'inversor',
      key: 'inversor',
      width: 95,
      // Sí / No como texto (regla 3 del manual: ni verde ni azul significan nada aquí).
      render: (v: boolean) => (v ? 'Sí' : 'No'),
    },
    {
      title: 'Farmacia',
      dataIndex: 'titularFarmacia',
      key: 'titularFarmacia',
      width: 100,
      render: (v: boolean) => (
        <Tooltip title="Titular vigente de al menos una farmacia">
          <span>{v ? 'Sí' : 'No'}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Fecha alta',
      dataIndex: 'fechaAlta',
      key: 'fechaAlta',
      width: 120,
      className: 'fct-num',
      align: 'right',
      // La fecha de baja ya se indica en el nombre (tooltip + resaltado de fila, D-GC-73); aquí
      // solo la fecha de alta, sin duplicar la marca.
      render: (v: string | null) => (v ? fmtFecha(v) : <Text type="secondary">—</Text>),
    },
  ];
  // Sin columna "Acciones": editar/eliminar se moverán a la ficha del cliente (clic en fila o en
  // celdas concretas — pendiente de decidir, ajuste del Dev 2026-09-18). Retirados EditOutlined/
  // DeleteOutlined de los imports; A7 (edición aplazada) y A6 (bajas sin borrado físico) siguen
  // vigentes, solo cambia dónde se expresan en la UI.

  return (
    <GcPagina
      seccion="Clientes"
      titulo="Clientes"
      densidad="compacta"
      acciones={
        <>
          <Tooltip title="Herramienta señalada como peligrosa en la operativa actual — fuera de v1">
            <Button icon={<ExportOutlined />} disabled>Exportar a Excel</Button>
          </Tooltip>
          {/* Ajuste del Dev, 2026-09-18: "Crear cliente" a la cabecera (antes en la banda de
              filtros) — no es un filtro, es la acción de alta directa. */}
          <CrearClienteDrawer
            catalogos={catalogos}
            onCreado={(r) => {
              setRecienCreado(r.idCliente);
              // Tras crear, se muestra el listado aunque el usuario no hubiera buscado nada
              // todavía (quiere ver el cliente que acaba de crear, no una pantalla vacía).
              // El alta nueva es la más reciente: con el orden por defecto cae en la primera
              // página. Si ya estábamos ahí (con haBuscado ya en true), cambiar de página no
              // dispara la recarga por sí solo, así que se pide a mano; en cualquier otro caso
              // (primera carga o cambio de página), el propio cambio de estado la dispara.
              const yaEnPrimeraPaginaCargada = haBuscado && pagina === 1;
              setHaBuscado(true);
              setPagina(1);
              if (yaEnPrimeraPaginaCargada) cargar();
            }}
          />
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/altas')}>
            Altas de clientes
          </Button>
          {/* GESCL-T07 (tramo 5): acceso a la herramienta de duplicados solo para quien puede
              gestionar altas (Clientes.Altas.Gestionar, D-GC-32); la ruta lo vuelve a comprobar. */}
          {hasPermission('Clientes.Altas.Gestionar') && (
            <Button icon={<MergeCellsOutlined />} onClick={() => navigate('/clientes/duplicados')}>
              Duplicados
            </Button>
          )}
        </>
      }
    >
      <FctTarjeta>
        <div className="fct-filtros" style={{ justifyContent: 'flex-end', marginBottom: 16 }}>
          <Input.Search
            placeholder="Nombre y apellidos, o email..."
            prefix={<SearchOutlined />}
            style={{ width: 260 }}
            allowClear
            value={pendientes.busqueda}
            onChange={(e) => patch({ busqueda: e.target.value })}
            onSearch={buscarSiNoEsLimpiar((v) => aplicar({ busqueda: v.trim() }))}
          />
          <Input.Search
            placeholder="ID o código"
            style={{ width: 160 }}
            allowClear
            value={pendientes.idCodigo}
            onChange={(e) => patch({ idCodigo: e.target.value })}
            onSearch={buscarSiNoEsLimpiar((v) => aplicar({ idCodigo: v.trim() }))}
          />
          <Select
            style={{ width: 170 }}
            showSearch
            optionFilterProp="label"
            value={pendientes.idProvincia}
            onChange={(v) => patch({ idProvincia: v })}
            options={opcionesProvincia}
          />
          <Select
            style={{ width: 150 }}
            value={pendientes.inversor}
            onChange={(v) => patch({ inversor: v })}
            options={[
              { label: 'Inversor: todos', value: TODOS },
              { label: 'Inversor: Sí', value: 1 },
              { label: 'Inversor: No', value: 0 },
            ]}
          />
          <Select
            style={{ width: 160 }}
            value={pendientes.farmacia}
            onChange={(v) => patch({ farmacia: v })}
            options={[
              { label: 'Farmacia: todos', value: TODOS },
              { label: 'Farmacia: Sí', value: 1 },
              { label: 'Farmacia: No', value: 0 },
            ]}
          />
          <Select
            style={{ width: 175 }}
            value={pendientes.tipoRegistro}
            onChange={(v) => patch({ tipoRegistro: v })}
            options={[
              { label: 'Todos los registros', value: TipoRegistroGrid.Todos },
              { label: 'Solo definitivos', value: TipoRegistroGrid.Definitivos },
              { label: 'Solo provisionales', value: TipoRegistroGrid.Provisionales },
              { label: 'Solo de baja', value: TipoRegistroGrid.DeBaja },
            ]}
          />
          {/* Filtros de "segundo nivel" (ajuste del Dev, 2026-09-18): Clasificación y Teléfono, de
              uso más ocasional — en un popover para no ensanchar la banda principal. El punto azul
              en el botón marca que hay uno APLICADO ahora mismo (no solo tecleado). */}
          <Popover
            title="Más filtros"
            trigger="click"
            open={popoverAbierto}
            onOpenChange={setPopoverAbierto}
            content={
              <Space direction="vertical" size={12} style={{ width: 280 }}>
                <div>
                  <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Clasificación</Text>
                  <Select
                    style={{ width: '100%' }}
                    value={pendientes.clasificacion}
                    onChange={(v) => patch({ clasificacion: v })}
                    options={[
                      { label: 'Todas', value: TODOS },
                      ...Object.entries(CLASIFICACION_LABELS).map(([id, label]) => ({
                        label,
                        value: Number(id),
                      })),
                    ]}
                  />
                </div>
                <div>
                  <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Teléfono</Text>
                  <Input.Search
                    placeholder="Busca entre todos los teléfonos"
                    allowClear
                    value={pendientes.telefono}
                    onChange={(e) => patch({ telefono: e.target.value })}
                    onSearch={buscarSiNoEsLimpiar((v) => {
                      aplicar({ telefono: v.trim() });
                      setPopoverAbierto(false);
                    })}
                  />
                </div>
                <div>
                  <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Código postal</Text>
                  <Input.Search
                    placeholder="Empieza por..."
                    allowClear
                    value={pendientes.codigoPostal}
                    onChange={(e) => patch({ codigoPostal: e.target.value })}
                    onSearch={buscarSiNoEsLimpiar((v) => {
                      aplicar({ codigoPostal: v.trim() });
                      setPopoverAbierto(false);
                    })}
                  />
                </div>
                <div>
                  <Text style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Población</Text>
                  <Input.Search
                    placeholder="Contiene..."
                    allowClear
                    value={pendientes.poblacion}
                    onChange={(e) => patch({ poblacion: e.target.value })}
                    onSearch={buscarSiNoEsLimpiar((v) => {
                      aplicar({ poblacion: v.trim() });
                      setPopoverAbierto(false);
                    })}
                  />
                </div>
                {/* Mismo rojo que el resaltado de fila de un cliente de baja (D-GC-73): el check
                    "avisa" de que se van a ver mezclados con el resto. */}
                <div
                  style={pendientes.incluirBajas
                    ? { background: fct.estado.riesgo.relleno, border: `1px solid ${fct.estado.riesgo.linea}`, borderRadius: 4, padding: '4px 8px' }
                    : undefined}
                >
                  <Checkbox
                    checked={pendientes.incluirBajas}
                    onChange={(e) => patch({ incluirBajas: e.target.checked })}
                  >
                    <Text style={pendientes.incluirBajas ? { color: fct.estado.riesgo.texto, fontWeight: 600 } : undefined}>
                      Incluir bajas
                    </Text>
                  </Checkbox>
                </div>
                <Button
                  block
                  onClick={() => {
                    aplicar();
                    setPopoverAbierto(false);
                  }}
                >
                  Aplicar
                </Button>
              </Space>
            }
          >
            {/* Rojo (mismo que las bajas) cuando lo aplicado incluye bajas; neutro para el resto de
                filtros de 2º nivel (el verde es marca, no significado: R-10). Se basa en `aplicados`,
                no en `pendientes`: es un indicador de lo que está afectando al listado ahora mismo,
                igual que el resto de marcas del grid. */}
            <Badge
              dot={haySegundoNivelAplicado}
              color={aplicados.incluirBajas ? fct.estado.riesgo.linea : fct.estado.neutro.linea}
            >
              <Button
                danger={aplicados.incluirBajas}
              >
                ...
              </Button>
            </Badge>
          </Popover>
          {/* El punto avisa de que hay algo tecleado o seleccionado que aún no se ha consultado. */}
          <Badge dot={hayCambiosSinAplicar}>
            <Button type="primary" icon={<SearchOutlined />} onClick={() => aplicar()} loading={cargando}>
              Buscar
            </Button>
          </Badge>
          <Button onClick={limpiar} disabled={filtrosAplicados.length === 0 && !hayCambiosSinAplicar}>
            Limpiar
          </Button>
        </div>

        {!haBuscado ? (
          <Empty
            description="Use los filtros de arriba y pulse Buscar (o Enter en el texto/ID) para consultar la base de clientes."
            style={{ margin: '48px 0' }}
          />
        ) : (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
              Mostrando <Text strong className="fct-mono">{fmtNumero(totalFiltrado)}</Text> de{' '}
              <Text strong className="fct-mono">{fmtNumero(totalBase)}</Text> clientes
              {filtrosAplicados.length > 0 && <> · filtrado por {filtrosAplicados.join(' + ')}</>}
            </Text>

            <Table<ClienteGridFila>
              rowKey="idCliente"
              size="middle"
              loading={cargando}
              columns={columns}
              dataSource={clientes}
              pagination={{
                current: pagina,
                pageSize: tamanio,
                total: totalFiltrado,
                pageSizeOptions: ['20', '50', '100'],
                showSizeChanger: true,
                showTotal: (t) => `${fmtNumero(t)} clientes`,
                onChange: (p, ps) => {
                  setPagina(p);
                  setTamanio(ps);
                },
              }}
              onRow={(c) => ({
                // Cliente de baja (D-GC-73): fondo resaltado en toda la fila — visible sin tener
                // que fijarse en una columna concreta. Solo aparece con "Incluir bajas" marcado, o
                // con el filtro "Solo de baja" (ver script 026).
                style: {
                  cursor: c.codigo ? 'pointer' : 'default',
                  ...(c.fechaBaja ? { backgroundColor: fct.estado.riesgo.relleno } : {}),
                },
                // Ficha del cliente en el portal Gescom (Solusoft) — fuera de GCvsc, por eso se
                // abre en pestaña nueva (no perder el listado ni su paginación). No es el ID
                // Gescom (`idCliente`): el portal identifica por REFCCL = código GF (CL…/PR…).
                onClick: () => {
                  if (!c.codigo) return;
                  window.open(
                    `https://farma-gescom-portal-pro.azurewebsites.net/Cliente?refccl=${encodeURIComponent(c.codigo)}`,
                    '_blank',
                    'noopener,noreferrer',
                  );
                },
              })}
              style={{ background: fct.superficie.tarjeta }}
            />
          </>
        )}
      </FctTarjeta>
    </GcPagina>
  );
};

export default ClientesGridPage;
